import React, { useCallback } from 'react'
import { useDropzone, Accept } from 'react-dropzone'
import { Button } from '../button/Button.tsx'
import { cn } from '@/lib/utils.ts'

export interface UploadFile {
  file: File
  previewUrl: string
}

interface FileUploadProps {
  onUpload: (files: UploadFile[]) => void
  maxFileSize?: number
  title?: string
  description?: string
  uploadButtonText?: string
  dragDropText?: string
  orText?: string
  maxFileSizeText?: string
  allowMultiple?: boolean
  className?: string
  errorMessages?: {
    fileSizeExceeded?: string
  }
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onUpload,
  maxFileSize = 50,
  uploadButtonText = 'Browse Files',
  dragDropText = 'Drag and drop your file here',
  orText = 'or',
  maxFileSizeText = 'Maximum file size: 50MB',
  allowMultiple = false,
  className,
}) => {
  const handleFileProcessing = useCallback(
    (files: File[]) => {
      const processedFiles: UploadFile[] = files.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }))
      onUpload(processedFiles)
    },
    [onUpload]
  )

  const acceptedFileTypes: Accept = {
    'application/pdf': ['.pdf'],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
          // If multiple files are not allowed, only process the first file
          const filesToProcess = allowMultiple
            ? acceptedFiles
            : [acceptedFiles[0]]
          handleFileProcessing(filesToProcess)
        }
      },
      [handleFileProcessing, allowMultiple]
    ),
    accept: acceptedFileTypes,
    maxSize: maxFileSize * 1024 * 1024,
    multiple: allowMultiple,
  })

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={cn(
          'mb-4 w-full p-10 rounded-lg flex flex-col justify-center border border-dashed cursor-pointer transition-colors duration-200',
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 bg-gray-50',
          className
        )}
      >
        <input {...getInputProps()} />
        <div className="flex justify-center"></div>
        <div className="p-2 text-center">
          <p className="text-gray-600 font-semibold mb-1">
            {isDragActive ? 'Drop the file here' : dragDropText}
          </p>
          <p className="text-gray-600 text-sm">{maxFileSizeText}</p>
        </div>
        <div className="flex items-center my-4">
          <hr className="flex-grow border-gray-300" />
          <span className="mx-2 text-gray-400 text-sm">{orText}</span>
          <hr className="flex-grow border-gray-300" />
        </div>
        <div className="text-center flex justify-center">
          <Button type="button" variant="outline" size="sm">
            {uploadButtonText}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FileUpload
